import html2canvas, { Options } from 'html2canvas';
import { saveFileAs } from './file';

/**
 * HTML 转图片下载配置项
 */
export interface Html2ImageOptions extends Partial<Options> {
  /** 缩放比例，默认 1.5 */
  scale?: number;
  /** 背景色 */
  backgroundColor?: string;
  /** 是否允许跨域污染画布（默认 false，设为 true 可能导致 toBlob 失败） */
  allowTaint?: boolean;
}

export function imgToBase64(url: string, defaultImage?: string) {
  if (!url) return url || defaultImage;
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  const img = new Image();
  img.crossOrigin = 'Anonymous';
  img.src = url;
  return new Promise((resolve, reject) => {
    img.onload = function () {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx?.drawImage(img, 0, 0);
      const dataURL = canvas.toDataURL('image/png');
      resolve(dataURL);
    };
    img.onerror = function (err) {
      if (defaultImage) return resolve(defaultImage);
      else reject(err);
    };
  }).catch(error => {
    console.log(error);
  });
}

/**
 * 检测是否为 Safari 或 WebKit 浏览器
 */
const isSafariBrowser = (): boolean => {
  const ua = navigator.userAgent;
  return (
    /^((?!chrome|android).)*safari/i.test(ua) || ua.includes('AppleWebKit')
  );
};

/**
 * Safari 在 ctx.createPattern() 接收跨域 HTMLImageElement（尤其是 SVG 背景图）时
 * 会抛出 "Operation is insecure" 安全错误。
 *
 * html2canvas 的 CanvasRenderer.resizeImage 在宽高匹配时会直接返回原始 HTMLImageElement，
 * 再传给 createPattern —— Safari 会拒绝跨域图片。
 *
 * 修复方案：在 html2canvas 执行前，临时 patch 全局 CanvasRenderingContext2D.prototype.createPattern，
 * 将 HTMLImageElement 先绘制到中间 canvas 上，再传给原始 createPattern。
 * html2canvas 执行完毕后立即恢复原始方法。
 *
 * 注意：不能通过 html2canvas 的 `canvas` 选项传入自定义 canvas，
 * 因为 html2canvas 在检测到 options.canvas 存在时会跳过设置 canvas 宽高（源码第 6618 行），
 * 导致 canvas 使用默认的 300×150 尺寸，截图只有左上角。
 */
function installCreatePatternPatch() {
  const proto = CanvasRenderingContext2D.prototype;
  const original = proto.createPattern;

  proto.createPattern = function patchedCreatePattern(
    this: CanvasRenderingContext2D,
    image: CanvasImageSource,
    repetition: string | null,
  ) {
    if (image instanceof HTMLImageElement) {
      const sw = image.naturalWidth || image.width || 1;
      const sh = image.naturalHeight || image.height || 1;
      const tmpCanvas = document.createElement('canvas');
      tmpCanvas.width = sw;
      tmpCanvas.height = sh;
      const tmpCtx = tmpCanvas.getContext('2d');
      if (tmpCtx) {
        tmpCtx.drawImage(image, 0, 0, sw, sh);
      }
      return original.call(this, tmpCanvas, repetition);
    }
    return original.call(this, image, repetition);
  } as typeof proto.createPattern;

  // Return a restore function
  return () => {
    proto.createPattern = original;
  };
}

/**
 * 将 HTML 元素转换为图片并下载
 * @param element - 要转换的 DOM 元素
 * @param fileName - 下载的文件名（不含扩展名），默认 'screenshot'
 * @param options - html2canvas 配置项
 * @returns Promise<boolean> - 下载是否成功
 */
export async function downloadHtmlToImage(
  element: HTMLElement | null,
  fileName = 'screenshot',
  options: Html2ImageOptions = {},
): Promise<boolean> {
  if (!element) {
    console.error('[downloadHtmlToImage] 元素不存在');
    return false;
  }

  const isSafari = isSafariBrowser();

  // Temporarily patch createPattern for Safari compatibility
  // Must be installed before html2canvas runs and restored after
  const restoreCreatePattern = isSafari ? installCreatePatternPatch() : null;

  try {
    // 检查 allowTaint 配置并给出警告
    if (options.allowTaint === true) {
      console.warn(
        '[downloadHtmlToImage] allowTaint: true 可能导致 toBlob 失败，推荐使用 allowTaint: false 并确保图片服务器支持 CORS',
      );
    }

    const canvas = await html2canvas(element, {
      // 跨域配置：优先使用 CORS，不允许污染（确保可导出）
      useCORS: true, // 使用 CORS 加载跨域图片
      allowTaint: false, // 默认禁止污染画布（否则 toBlob 会报 SecurityError）
      // 图片加载超时配置
      imageTimeout: 30 * 1000, // 图片加载超时 30 秒
      // 尺寸配置 - Safari 降低缩放避免渲染问题
      scale: options.scale ?? (isSafari ? 1 : 1.5),
      width: element.offsetWidth,
      height: element.offsetHeight,
      // 渲染配置 - 禁用 foreignObjectRendering 以确保多个 Canvas 图表能正确截图
      // foreignObjectRendering 可能导致 ECharts Canvas 无法正确克隆
      foreignObjectRendering: false,
      logging: false, // 关闭日志
      // 元素过滤
      ignoreElements: el => {
        // 忽略 iframe
        if (el.tagName === 'IFRAME') return true;
        // 忽略 script 标签
        if (el.tagName === 'SCRIPT') return true;
        return false;
      },
      // Safari/WebKit 兼容性优化
      removeContainer: true, // 渲染后移除临时容器，避免内存泄漏
      // windowWidth: element.scrollWidth,
      // windowHeight: element.scrollHeight,
      scrollX: 0,
      scrollY: 0,
      backgroundColor: options.backgroundColor ?? '#ffffff',
      ...options,
    });

    await new Promise<void>((resolve, reject) => {
      try {
        canvas.toBlob(
          (blob: Blob | null) => {
            if (blob) {
              saveFileAs(blob, `${fileName}.png`);
              resolve();
            } else {
              reject(new Error('Canvas 转换为 Blob 失败'));
            }
          },
          'image/png',
          0.95, // 图片质量
        );
      } catch (err) {
        // Canvas 可能被污染，尝试降级处理
        reject(new Error(`Canvas 导出失败: ${err}`));
      }
    });

    return true;
  } catch (error) {
    console.error('[downloadHtmlToImage] 下载失败:', error);
    return false;
  } finally {
    // Restore original createPattern to avoid polluting global scope
    restoreCreatePattern?.();
  }
}

export function judgeImage(url: string) {
  const img = new Image();
  img.crossOrigin = 'Anonymous';
  img.src = url;
  return new Promise((resolve, reject) => {
    img.onload = function () {
      resolve(true);
    };
    img.onerror = function (err) {
      resolve(false);
    };
  });
}
